class CollectionSection extends HTMLElement {
  constructor() {
    super();

    this.pageOverlayClass = "page-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.classDrawerActive = "wt-filter--drawer-open";
    this.cachedDrawer = null;
    this.cachedCloseButton = null;
    this.cachedTrigger = null;
    this.cachedSectionsTriggers = null;
    this.isDrawer = this.dataset.filterPosition === "drawer";

    this.triggerClasses = [
      "wt-filter__close",
      this.pageOverlayClass,
      "collection__filter-trigger",
    ];

    this.overlay = document.createElement("div");

    this.breakpoint = 1200;
    this.currentDrawerMode = this.isDrawerMode();

    this.init();
  }

  drawer() {
    if (!this.cachedDrawer) {
      this.cachedDrawer = document.querySelector(".wt-filter");
    }
    return this.cachedDrawer;
  }

  getCloseButton() {
    if (!this.cachedCloseButton) {
      this.cachedCloseButton = document.querySelector(".wt-filter__close");
    }
    return this.cachedCloseButton;
  }

  getTrigger() {
    if (!this.cachedTrigger) {
      this.cachedTrigger = document.querySelector(".collection__filter-trigger");
    }
    return this.cachedTrigger;
  }

  sectionsTriggers() {
    const drawer = this.drawer();
    if (!drawer) return null;
    if (!this.cachedSectionsTriggers) {
      this.cachedSectionsTriggers = drawer.querySelectorAll(".wt-collapse__trigger");
    }
    return this.cachedSectionsTriggers;
  }

  isOpen() {
    return document.body.classList.contains(this.activeOverlayBodyClass);
  }

  toggleDrawerElements() {
    const drawer = this.drawer();
    if (!drawer) return null;
    return drawer.querySelectorAll(drawer.dataset.toggleTabindex);
  }

  init() {
    this.createOverlay();

    document.body.addEventListener("click", (e) => {
      if (this.triggerClasses.some((cls) => e.target.classList.contains(cls))) {
        this.toggleDrawer(e);
      }
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen()) {
          this.toggleDrawer(e);
        }
      }

      if (isTabPressed && this.isOpen() && this.currentDrawerMode) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    window.addEventListener("resize", this.handleResize.bind(this));
    this.handleResize();
  }

  isDrawerMode() {
    const width = window.innerWidth;
    return this.isDrawer || width <= this.breakpoint;
  }

  handleResize() {
    const isDrawerMode = this.isDrawerMode();

    if (isDrawerMode !== this.currentDrawerMode) {
      this.currentDrawerMode = isDrawerMode;

      if (isDrawerMode) {
        if (!this.isOpen()) {
          const drawer = this.drawer();
          if (drawer) {
            drawer.classList.remove(this.classDrawerActive);
          }
          document.body.classList.remove(this.activeOverlayBodyClass);
        }
        this.updateTabindexes(this.isOpen());
      } else {
        this.drawer()?.classList.add(this.classDrawerActive);
        document.body.classList.remove(this.activeOverlayBodyClass);
        this.updateTabindexes(true);
      }
    }
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  getFocusableElements() {
    const drawer = this.drawer();
    if (!drawer) return { focusableElements: [], first: null, last: null };

    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const elements = Array.from(
      drawer.querySelectorAll(focusableElementsSelector),
    ).filter(
      (el) =>
        !el.hasAttribute("disabled") &&
        el.tabIndex >= 0 &&
        el.offsetParent !== null,
    );

    return {
      focusableElements: elements,
      first: elements[0],
      last: elements[elements.length - 1],
    };
  }

  updateTabindexes(isOpen) {
    if (this.currentDrawerMode) {
      if (isOpen) {
        this.getCloseButton()?.focus();
        this.temporaryHideFocusVisible();
        setTabindex(this.sectionsTriggers(), "0");
        setTabindex(this.toggleDrawerElements(), "0");
      } else {
        this.getTrigger()?.focus();
        this.temporaryHideFocusVisible();
        setTabindex(this.sectionsTriggers(), "-1");
        setTabindex(this.toggleDrawerElements(), "-1");

        this.closeAllCollapsibleSections();
      }
    } else {
      // Always visible mode
      setTabindex(this.sectionsTriggers(), "0");
      setTabindex(this.toggleDrawerElements(), "0");
    }
  }

  toggleDrawer(e) {
    if (e) e.preventDefault();

    if (!this.currentDrawerMode) {
      return;
    }

    if (this.isOpen()) {
      // close drawer
      const offsetTop = -parseInt(document.body.style.top, 10);
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      window.scrollTo(0, offsetTop);

      this.closeAllCollapsibleSections();
    } else {
      // open drawer
      document.body.style.top = `${-document.documentElement.scrollTop}px`;
      document.body.style.left = "0px";
    }

    this.drawer()?.classList.toggle(this.classDrawerActive);
    document.body.classList.toggle(this.activeOverlayBodyClass);

    this.updateTabindexes(this.isOpen());
  }

  closeAllCollapsibleSections() {
    const openSections = this.drawer().querySelectorAll('[data-open="true"]');
    openSections.forEach((section) => {
      const trigger = section.querySelector(".wt-collapse__trigger");
      if (trigger) {
        trigger.classList.remove("wt-collapse__trigger--active");
        section.dataset.open = "false";
        const focusableElementsWithTabindex =
          section.querySelectorAll('[tabindex="0"]');
        setTabindex(focusableElementsWithTabindex, "-1");
      }
    });
  }

  createOverlay() {
    if (!document.querySelector(`.${this.pageOverlayClass}`)) {
      this.overlay?.classList.add(this.pageOverlayClass);
      document.body.appendChild(this.overlay);
    }
  }
}

customElements.define("collection-section", CollectionSection);
// Load More Products Functionality
// Uses event delegation so the button still works after filter updates replace the grid/button DOM.
class LoadMoreProducts {
  constructor() {
    this.productsPerPage = null;
    this.currentlyVisible = null;
    this.init();
  }

  init() {
    document.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    const loadMoreBtn = event.target.closest('#load-more-btn');
    if (!loadMoreBtn) return;

    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    // Search page: load next page via fetch and append results (no full reload, no scroll to top)
    if (loadMoreBtn.dataset.nextUrl) {
      event.preventDefault();
      this.loadSearchNextPage(loadMoreBtn);
      return;
    }

    // Collection page: reveal next batch of already-rendered products
    this.productsPerPage =
      parseInt(productGrid.dataset.productsPerPage, 10) || 24;
    const allProducts = productGrid.querySelectorAll('.collection__product-item');
    const visibleCount = productGrid.querySelectorAll(
      '.collection__product-item.visible',
    ).length;

    this.currentlyVisible = visibleCount;
    const nextBatch = this.currentlyVisible + this.productsPerPage;

    for (
      let i = this.currentlyVisible;
      i < nextBatch && i < allProducts.length;
      i++
    ) {
      allProducts[i].classList.add('visible');
    }

    this.currentlyVisible = Math.min(nextBatch, allProducts.length);

    if (this.currentlyVisible >= allProducts.length) {
      // If there's a next page (collection pagination), switch to fetch mode and keep button visible
      if (loadMoreBtn.dataset.nextPageUrl) {
        loadMoreBtn.dataset.nextUrl = loadMoreBtn.dataset.nextPageUrl;
      } else {
        loadMoreBtn.style.display = 'none';
      }
    }
  }

  loadSearchNextPage(loadMoreBtn) {
    const nextUrl = loadMoreBtn.dataset.nextUrl;
    if (!nextUrl) return;

    const loadMoreWrapper = loadMoreBtn.closest('.collection__load-more');
    const originalText = loadMoreBtn.textContent;

    loadMoreBtn.disabled = true;
    loadMoreBtn.textContent = loadMoreBtn.dataset.loadingText || 'Loading...';

    fetch(nextUrl, { headers: { Accept: 'text/html' } })
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sourceGrid = doc.getElementById('product-grid');
        const currentGrid = document.getElementById('product-grid');
        if (!sourceGrid || !currentGrid) return;

        // Append new product/grid items (search uses .collection__grid__item)
        const newItems = sourceGrid.querySelectorAll('.collection__grid__item');
        newItems.forEach((el) => currentGrid.appendChild(el.cloneNode(true)));

        // Re-run any scroll/animation triggers if present
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(currentGrid.innerHTML); 
        }
        // Re-initialize pill horizontal drag-scroll on newly appended cards
        if (typeof initCardSizePillsDragScroll === 'function') {
          initCardSizePillsDragScroll();
        }

        // Update the Show more button: use next page URL from response (collection) or nextUrl (search)
        const nextLoadMore = doc.querySelector('.collection__load-more');
        const nextBtn = nextLoadMore?.querySelector('#load-more-btn');
        if (!nextLoadMore && loadMoreWrapper) {
          loadMoreWrapper.remove();
        } else if (loadMoreWrapper) {
          if (nextBtn?.dataset.nextPageUrl) {
            loadMoreBtn.dataset.nextPageUrl = nextBtn.dataset.nextPageUrl;
            delete loadMoreBtn.dataset.nextUrl;
          } else if (nextBtn?.dataset.nextUrl) {
            loadMoreBtn.dataset.nextUrl = nextBtn.dataset.nextUrl;
          } else {
            delete loadMoreBtn.dataset.nextUrl;
            delete loadMoreBtn.dataset.nextPageUrl;
          }
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = originalText;
          if (!nextBtn?.dataset.nextUrl && !nextBtn?.dataset.nextPageUrl) {
            loadMoreBtn.style.display = 'none';
          }
        } else {
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = originalText;
          loadMoreBtn.style.display = 'none';
        }
      })
      .catch(() => {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = originalText;
        window.location.href = nextUrl;
      });
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LoadMoreProducts();
  });
} else {
  new LoadMoreProducts();
}